import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getCustomerOnboardingDetails, completeCustomerOnboarding } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  CheckCircle,
  Shield,
  Building2,
  User,
  Lock,
  Loader2,
  AlertTriangle,
  Eye,
  EyeOff,
  ArrowRight,
  FileText,
  ClipboardCheck,
  Sparkles
} from 'lucide-react';

export default function CustomerOnboarding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadDetails = async () => {
      if (!token) {
        setError('No onboarding token provided');
        setLoading(false);
        return;
      }
      
      try {
        const response = await getCustomerOnboardingDetails(token);
        setDetails(response.data);
      } catch (err) {
        console.error('Error loading onboarding details:', err);
        setError(err.response?.data?.detail || 'Failed to load onboarding details');
      } finally {
        setLoading(false);
      }
    };
    
    loadDetails();
  }, [token]);

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const validatePassword = () => {
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) return;
    
    setSubmitting(true);
    try {
      const response = await completeCustomerOnboarding(token, password);
      
      // Log the user in
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Account created successfully! Welcome to Players Health.');
      
      // Navigate to dashboard
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 500);
    } catch (err) {
      console.error('Error completing onboarding:', err);
      toast.error(err.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading your account details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-slate-100 py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_safetyguard-4/artifacts/97rz3phl_players-health-lockup-color.png"
              alt="Players Health"
              className="h-10 mx-auto"
            />
          </div>
          
          <Card className="shadow-xl border-0">
            <CardContent className="pt-8 pb-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold mb-2">Unable to Complete Setup</h2>
                <p className="text-slate-600 mb-6">{error}</p>
                <div className="space-y-3">
                  <Button onClick={() => navigate('/login')} className="w-full">
                    Go to Login
                  </Button>
                  <p className="text-sm text-slate-500">
                    Need help? Contact your broker or <a href="mailto:support@playershealth.com" className="text-blue-600 hover:underline">support@playershealth.com</a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-slate-100 py-4 sm:py-8 px-3 sm:px-4">
      <div className="max-w-2xl mx-auto">
        {/* Logo */}
        <div className="text-center mb-6 sm:mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_safetyguard-4/artifacts/97rz3phl_players-health-lockup-color.png"
            alt="Players Health"
            className="h-8 sm:h-10 mx-auto"
          />
        </div>

        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 text-white shadow-lg">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 text-center sm:text-left">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 sm:w-7 sm:h-7" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Welcome to Players Health!</h1>
              <p className="text-blue-100 text-sm sm:text-base">Complete your account setup to access your risk management dashboard</p>
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
          {/* Left Column - What's Preserved */}
          <div className="space-y-4">
            {/* Organization Card */}
            <Card className="shadow-md border-0">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Your Organization
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="font-semibold text-lg">{details?.organization?.name}</p>
                  <p className="text-sm text-slate-500 capitalize">{details?.organization?.org_type} Organization</p>
                  {details?.organization?.sport_type && (
                    <Badge variant="outline">{details.organization.sport_type}</Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Risk Assessment */}
            {details?.assessment?.score && (
              <Card className="shadow-md border-0">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-green-600" />
                    Risk Assessment Preserved
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Risk Score</p>
                      <p className="text-3xl font-bold text-blue-600">{details.assessment.score}%</p>
                    </div>
                    <Badge className={`${getRiskColor(details.assessment.risk_level)} border px-3 py-1`}>
                      {details.assessment.risk_level?.toUpperCase()} RISK
                    </Badge>
                  </div>
                  {details.assessment.recommendations?.length > 0 && (
                    <p className="text-sm text-slate-500 mt-3">
                      {details.assessment.recommendations.length} safety recommendations ready
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Documents */}
            {(details?.documents?.policies > 0 || details?.documents?.loss_runs > 0) && (
              <Card className="shadow-md border-0">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    Documents Preserved
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">{details.documents.policies}</p>
                      <p className="text-xs text-slate-500">Insurance Policies</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">{details.documents.loss_runs}</p>
                      <p className="text-xs text-slate-500">Loss Runs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Account Setup */}
          <Card className="shadow-xl border-0">
            <CardHeader className="text-center border-b pb-4">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="w-7 h-7 text-blue-600" />
              </div>
              <CardTitle className="text-xl">Create Your Account</CardTitle>
              <CardDescription>
                Set up your password to access all platform features
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-6 space-y-5">
              {/* Pre-filled Info */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium">{details?.name || 'Account Administrator'}</p>
                    <p className="text-sm text-slate-500">{details?.email}</p>
                  </div>
                </div>
              </div>

              {/* Password Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="password">Create Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter a secure password"
                      className="pl-10 pr-10"
                      required
                      minLength={8}
                      data-testid="input-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      className="pl-10"
                      required
                      data-testid="input-confirm-password"
                    />
                  </div>
                </div>

                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    You'll be the <strong>Organization Administrator</strong> with full access to manage 
                    your organization's risk profile, incidents, and training.
                  </AlertDescription>
                </Alert>

                <Button 
                  type="submit" 
                  className="w-full h-12 text-base" 
                  disabled={submitting}
                  data-testid="btn-complete"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Complete Setup & Access Dashboard
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Modules Unlocked */}
        <Card className="mt-4 sm:mt-6 shadow-md border-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-base sm:text-lg">Features You'll Have Access To</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {details?.modules_unlocked?.map((module, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                  <span className="text-xs sm:text-sm">{module}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 sm:mt-8 text-xs sm:text-sm text-slate-500">
          <p>Already have an account? <a href="/login" className="text-blue-600 hover:underline">Log in</a></p>
          <p className="mt-2">© 2026 Players Health. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
